/**
 * Vercel Web Analytics boot file.
 *
 * Responsibilities:
 *   - Skip entirely in SSR and in DEV mode (CA-21).
 *   - Inject Vercel Web Analytics only when the user has granted analytics consent.
 *   - Listen to the custom `korum:consent-change` window event to activate Vercel
 *     Analytics at runtime when the user updates their preferences.
 *
 * Notes:
 *   - `inject()` from `@vercel/analytics` auto-detects the deployment environment;
 *     no env variable is required (it relies on Vercel runtime rewrites in prod).
 *   - Effective consent withdrawal requires a full page reload — the script
 *     cannot be unloaded once mounted.
 *
 * Usage:
 *   import { trackEvent } from '@/boot/analytics';
 *   trackEvent('sign_up');
 *   trackEvent('debate_started', { protocol: 'devils-advocate' });
 */

import { boot } from 'quasar/wrappers';
import { inject, track } from '@vercel/analytics';
import { useConsent } from '@/composables/useConsent';

let injected = false;

/** Injects the Vercel Web Analytics runtime. Idempotent. */
const injectVercelAnalytics = (): void => {
  if (injected) return;
  // Skip unless Web Analytics is enabled on the Vercel project — otherwise
  // `/_vercel/insights/script.js` responds 403 and pollutes the console. The
  // flag is shared with Speed Insights (boot/speed-insights.ts).
  if (import.meta.env.VITE_ENABLE_VERCEL_INSIGHTS !== 'true') return;
  inject();
  injected = true;
};

/**
 * Track a custom Vercel Web Analytics event.
 * Safe to call before the analytics runtime is loaded — Vercel's `track()`
 * silently buffers calls until injection completes.
 *
 * @param name  - Event name (e.g. 'sign_up', 'debate_started').
 * @param props - Optional key/value properties visible in the Vercel dashboard.
 */
export const trackEvent = (name: string, props?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  // Vercel's `track()` typed signature constrains values to AllowedPropertyValues
  // (string | number | boolean | null | undefined). Our public contract is wider
  // (Record<string, unknown>) for backwards compatibility with existing
  // callsites; the cast is intentional and safe at runtime because the Vercel
  // runtime stringifies unsupported values.
  track(name, props as Parameters<typeof track>[1]);
};

export default boot(() => {
  // Skip SSR — window is not available.
  if (process.env.SERVER) return;

  // Skip in development — avoids polluting the Vercel Analytics dashboard (CA-21).
  if (import.meta.env.DEV) return;

  const { hasConsent } = useConsent();

  // Initial check — inject if consent already given (returning visitor).
  if (hasConsent('analytics')) {
    injectVercelAnalytics();
  }

  // React to runtime consent changes (e.g. user opens cookie preferences and toggles analytics).
  window.addEventListener('korum:consent-change', () => {
    if (hasConsent('analytics') && !injected) {
      injectVercelAnalytics();
    }
    // Note: removing the script after consent withdrawal is not implemented here
    // because the Vercel Analytics script is cookieless and does not perform cross-site tracking.
    // A full removal would require a page reload.
  });
});
